import React, { useCallback, useEffect, useState } from "react";
import { C } from "../constants.js";
import { I } from "../icons.jsx";
import { apiFetch, fetchStageDashboard } from "../api.js";
import { Spinner } from "../components/Spinner.jsx";

// `headerOnly` renders just the button and its modal, with no card
// chrome. Substep 5-π.2: this card and StageDocumentsCard each carried
// their own white card announcing the same current stage and offering
// the same stage's templates — one in bulk, one at a time — so the
// Documents tab read as two versions of the same thing. The button now
// lives in StageDocumentsCard's header, where the templates it
// generates are already listed.
export function StagePackageCard({ matterId, showNotification, headerOnly = false }) {
  const [stageInfo, setStageInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [batchPackage, setBatchPackage] = useState(null);
  const [batchLoadingPkg, setBatchLoadingPkg] = useState(false);
  const [batchSelections, setBatchSelections] = useState({});
  const [batchGenerating, setBatchGenerating] = useState(false);
  // Substep 5-π.1: default to holding documents for review. Batch
  // generation used to upload on generate, so a merge error — or a
  // test run — landed in a real client's Clio file and had to be
  // deleted by hand.
  const [reviewFirst, setReviewFirst] = useState(true);
  const [batchResult, setBatchResult] = useState(null);
  // When the batch result has skipped templates due to missing
  // fields, the modal surfaces an inline panel to fill those in
  // (deduped across templates) and retry only the skipped ones.
  const [batchOverrides, setBatchOverrides] = useState({});
  const [batchWriteBack, setBatchWriteBack] = useState(true);

  const loadStageInfo = useCallback(() => {
    fetchStageDashboard(matterId)
      .then(d => {
        setStageInfo({
          key: d.current_stage,
          label: d.current_stage_label,
          color: d.current_stage_info?.color,
        });
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [matterId]);
  useEffect(() => { loadStageInfo(); }, [loadStageInfo]);

  const openBatchModal = async () => {
    setShowBatchModal(true);
    setBatchLoadingPkg(true);
    setBatchResult(null);
    try {
      const resp = await apiFetch(`/api/matters/${matterId}/batch-package`);
      const pkg = resp.data;
      setBatchPackage(pkg);
      if (pkg && pkg.templates) {
        const sel = {};
        pkg.templates.forEach(t => {
          sel[t.filename] = t.default_on && t.can_generate;
        });
        setBatchSelections(sel);
      }
    } catch (e) {
      showNotification("Failed to load package: " + e.message, "error");
    }
    setBatchLoadingPkg(false);
  };

  // Pull a deduplicated list of missing-field descriptors out of
  // every skipped template in a batch result. Two templates that
  // both need {{attorney_email}} produce one entry, not two.
  const dedupeMissingFields = (result) => {
    if (!result?.results) return [];
    const seen = new Map();
    result.results.forEach(r => {
      if (r.status !== "skipped" || !r.missing_fields) return;
      r.missing_fields.forEach(f => {
        if (!seen.has(f.field)) seen.set(f.field, f);
      });
    });
    return Array.from(seen.values());
  };

  // Run a batch. `selectedFilenames` is the explicit list (used by
  // both the initial run and the "retry skipped" path). `opts.overrides`
  // and `opts.writeBack` flow into the request body for the retry path.
  const runBatch = async (selectedFilenames, opts = {}) => {
    if (!selectedFilenames || selectedFilenames.length === 0) {
      showNotification("Select at least one document to generate.", "warning");
      return;
    }
    setBatchGenerating(true);
    try {
      const body = {
        matter_id: matterId,
        selected_templates: selectedFilenames,
        // Substep 5-π.1: hold for attorney review rather than pushing
        // straight into the client's Clio file.
        review_before_upload: reviewFirst,
      };
      if (opts.overrides && Object.keys(opts.overrides).length > 0) {
        // Drop empty values — let validation re-flag them as missing
        // so the user knows they didn't actually fill it in.
        const filtered = Object.fromEntries(
          Object.entries(opts.overrides).filter(([, v]) => v !== "" && v !== null && v !== undefined)
        );
        if (Object.keys(filtered).length > 0) {
          body.field_overrides = filtered;
          body.write_back_to_clio = !!opts.writeBack;
        }
      }
      const resp = await apiFetch("/api/generate/batch", {
        method: "POST",
        body: JSON.stringify(body),
      });
      setBatchResult(resp);
      // Build user-friendly toast that reflects what actually happened.
      const wb = resp.clio_writeback;
      const writtenCount = wb?.written?.length || 0;
      // Say plainly where the documents went. "Generated" is
      // ambiguous when the whole point of the change is that they
      // did NOT reach Clio yet.
      const held = reviewFirst;
      let msg = held
        ? `${resp.summary.total_documents} document(s) ready for review in Drafts.`
        : `Batch complete: ${resp.summary.total_documents} document(s) uploaded to Clio.`;
      if (resp.summary.templates_skipped > 0) {
        msg += ` ${resp.summary.templates_skipped} skipped — fill in the missing fields below to retry.`;
      }
      if (writtenCount > 0) {
        msg += ` Saved ${writtenCount} field(s) back to the matter.`;
      }
      showNotification(msg, resp.summary.templates_skipped > 0 ? "warning" : "success");
      // Reset overrides so the next retry starts clean (unless still
      // missing, in which case the inline panel pre-fills with empty).
      const stillMissing = dedupeMissingFields(resp);
      const nextOverrides = {};
      stillMissing.forEach(f => { nextOverrides[f.field] = batchOverrides[f.field] || ""; });
      setBatchOverrides(nextOverrides);
      loadStageInfo();
    } catch (e) {
      showNotification("Batch generation failed: " + e.message, "error");
    }
    setBatchGenerating(false);
  };

  const handleBatchGenerate = async () => {
    setBatchResult(null);
    setBatchOverrides({});
    const selected = Object.entries(batchSelections).filter(([, v]) => v).map(([k]) => k);
    await runBatch(selected);
  };

  // Retry just the skipped templates with the overrides the user
  // typed into the inline panel. Same package, narrower target.
  const handleRetrySkipped = async () => {
    if (!batchResult) return;
    const skippedTemplates = batchResult.results
      .filter(r => r.status === "skipped" && r.missing_fields && r.missing_fields.length > 0)
      .map(r => r.template);
    if (skippedTemplates.length === 0) return;
    await runBatch(skippedTemplates, {
      overrides: batchOverrides,
      writeBack: batchWriteBack,
    });
  };

  if (loading) return null;

  const trigger = React.createElement("button", {
    onClick: openBatchModal,
    disabled: !stageInfo,
    title: stageInfo
      ? `Generate every default document for ${stageInfo.label} in one batch`
      : "Loading stage…",
    style: {
      padding:"8px 18px", borderRadius:"10px", border:"none",
      background: C.gold, color:"#fff", fontSize:"12px", fontWeight:700,
      cursor:"pointer", display:"flex", alignItems:"center", gap:"6px",
      letterSpacing:"0.02em", flexShrink:0,
    }
  }, I.zap, "Generate Package");

  const shell = (...children) => headerOnly
    ? React.createElement(React.Fragment, null, ...children)
    : React.createElement("div", {
        style: { background:"#fff", borderRadius:"12px", padding:"20px 24px", border:`1px solid ${C.warmGray}`, marginBottom:"20px" }
      }, ...children);

  return shell(
    !headerOnly && React.createElement("div", {
      key: "hdr",
      style: { display:"flex", justifyContent:"space-between", alignItems:"center", gap:"16px" }
    },
      React.createElement("div", null,
        React.createElement("h2", {
          style: { fontSize:"13px", color:C.textMuted, textTransform:"uppercase", letterSpacing:"0.08em", fontWeight:600, margin:0 }
        }, "Generate Package"),
        stageInfo && React.createElement("div", {
          style: { fontSize:"13px", color:C.navy, fontWeight:500, marginTop:"4px" }
        },
          "Current stage: ",
          React.createElement("span", { style: { color: stageInfo.color || C.gold, fontWeight:700 } }, stageInfo.label),
          React.createElement("span", { style: { color:C.textMuted, fontWeight:400 } }, " — generates all default documents for this stage in one batch."),
        ),
      ),
      trigger,
    ),

    headerOnly && trigger,

    // Batch Generation Modal — same UX as the previous in-StageTracker version
    showBatchModal && React.createElement("div", {
      role:"dialog", "aria-modal":"true", "aria-label":"Batch Document Generation",
      style: {
        position:"fixed", top:0, left:0, right:0, bottom:0, background:"rgba(0,0,0,0.5)",
        display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000,
      },
      onClick: (e) => { if (e.target === e.currentTarget && !batchGenerating) setShowBatchModal(false); },
    },
      React.createElement("div", {
        style: {
          background:"#fff", borderRadius:"16px", padding:"28px 32px", width:"560px", maxHeight:"80vh",
          display:"flex", flexDirection:"column", boxShadow:"0 20px 60px rgba(0,0,0,0.3)",
        }
      },
        React.createElement("div", { style: { display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"20px" } },
          React.createElement("div", null,
            React.createElement("h2", {
              style: { fontFamily:"'Playfair Display',serif", fontSize:"20px", fontWeight:600, color:C.navy, margin:0 }
            }, batchPackage?.name || "Generate Package"),
            batchPackage?.description && React.createElement("p", {
              style: { fontSize:"13px", color:C.textMuted, margin:"4px 0 0" }
            }, batchPackage.description),
          ),
          !batchGenerating && React.createElement("button", {
            onClick: () => setShowBatchModal(false),
            style: { border:"none", background:"none", cursor:"pointer", fontSize:"20px", color:C.textLight, padding:"4px" }
          }, "\u00d7"),
        ),
        batchLoadingPkg && React.createElement("div", { style: { padding:"40px", textAlign:"center" } }, React.createElement(Spinner, null)),
        !batchLoadingPkg && !batchPackage && React.createElement("div", {
          style: { padding:"20px", textAlign:"center", color:C.textMuted, fontSize:"14px" }
        }, "No document package defined for this stage."),
        !batchLoadingPkg && batchPackage && !batchResult && React.createElement("div", {
          style: { flex:1, overflowY:"auto", marginBottom:"20px" }
        },
          React.createElement("div", {
            style: { display:"flex", gap:"12px", marginBottom:"12px", paddingBottom:"10px", borderBottom:`1px solid ${C.warmGray}40` }
          },
            React.createElement("button", {
              onClick: () => {
                const sel = {};
                batchPackage.templates.forEach(t => { sel[t.filename] = t.can_generate; });
                setBatchSelections(sel);
              },
              style: { border:"none", background:"none", cursor:"pointer", fontSize:"12px", color:C.gold, fontWeight:600, padding:0 }
            }, "Select all"),
            React.createElement("button", {
              onClick: () => setBatchSelections({}),
              style: { border:"none", background:"none", cursor:"pointer", fontSize:"12px", color:C.textLight, fontWeight:500, padding:0 }
            }, "Select none"),
          ),
          batchPackage.templates.map(tpl =>
            React.createElement("label", {
              key: tpl.filename,
              style: {
                display:"flex", alignItems:"flex-start", gap:"10px", padding:"10px 0",
                borderBottom:`1px solid ${C.warmGray}20`,
                cursor: tpl.can_generate ? "pointer" : "not-allowed",
                opacity: tpl.can_generate ? 1 : 0.5,
              },
            },
              React.createElement("input", {
                type: "checkbox",
                checked: !!batchSelections[tpl.filename],
                disabled: !tpl.can_generate || batchGenerating,
                onChange: (e) => setBatchSelections(prev => ({ ...prev, [tpl.filename]: e.target.checked })),
                style: { marginTop:"3px", accentColor:C.gold },
              }),
              React.createElement("div", { style: { flex:1 } },
                React.createElement("div", { style: { fontSize:"13px", fontWeight:600, color:C.navy } }, tpl.name),
                React.createElement("div", { style: { display:"flex", gap:"8px", alignItems:"center", marginTop:"2px" } },
                  React.createElement("span", { style: { fontSize:"11px", color:C.textLight } }, tpl.category),
                  tpl.requires_provider && React.createElement("span", {
                    style: { fontSize:"10px", color:C.gold, fontWeight:600, background:`${C.gold}15`, padding:"1px 6px", borderRadius:"4px" }
                  }, tpl.can_generate ? `${tpl.provider_count} provider(s)` : "Needs providers"),
                ),
                tpl.blocked_reason && React.createElement("div", { style: { fontSize:"11px", color:C.error, marginTop:"2px" } }, tpl.blocked_reason),
                tpl.note && tpl.can_generate && React.createElement("div", { style: { fontSize:"11px", color:C.textMuted, marginTop:"2px", fontStyle:"italic" } }, tpl.note),
              ),
            )
          ),

          // Substep 5-π.1: review gate. On by default — these are
          // letters an attorney signs, going into a client's file.
          React.createElement("label", {
            style: {
              display:"flex", alignItems:"flex-start", gap:"9px",
              marginTop:"14px", paddingTop:"13px",
              borderTop:`1px solid ${C.warmGray}`, cursor:"pointer",
            }
          },
            React.createElement("input", {
              type:"checkbox", checked: reviewFirst,
              onChange: e => setReviewFirst(e.target.checked),
              style: { marginTop:"2px", cursor:"pointer" },
            }),
            React.createElement("div", null,
              React.createElement("div", {
                style: { fontSize:"13px", fontWeight:600, color:C.navy }
              }, "Hold for review before sending to Clio"),
              React.createElement("div", {
                style: { fontSize:"11px", color: reviewFirst ? C.textMuted : C.error, marginTop:"2px" }
              }, reviewFirst
                  ? "Documents land in Drafts below. Preview, edit, then approve to upload."
                  : "Documents upload straight into the client's Clio file with no review."),
            ),
          ),
        ),
        batchResult && React.createElement("div", { style: { flex:1, overflowY:"auto", marginBottom:"20px" } },
          // Summary banner \u2014 color shifts based on whether anything
          // was skipped (green = full success, gold = partial).
          (() => {
            const hasSkipped = batchResult.summary.templates_skipped > 0;
            const bg = hasSkipped ? `${C.gold}15` : C.successBg;
            const fg = hasSkipped ? C.goldDim : C.success;
            return React.createElement("div", { style: { padding:"16px", borderRadius:"10px", background:bg, marginBottom:"16px" } },
              React.createElement("div", { style: { fontSize:"15px", fontWeight:600, color:fg, marginBottom:"4px" } },
                batchResult.summary.total_documents === 0
                  ? "0 document(s) generated"
                  : (reviewFirst
                      ? `${batchResult.summary.total_documents} document(s) awaiting your review`
                      : `${batchResult.summary.total_documents} document(s) uploaded to Clio`),
              ),
              React.createElement("div", { style: { fontSize:"12px", color:fg } },
                `${batchResult.summary.templates_succeeded} succeeded, ${batchResult.summary.templates_skipped} skipped, ${batchResult.summary.templates_failed} failed \u2014 ${(batchResult.summary.generation_time_ms / 1000).toFixed(1)}s`,
              ),
              // Surface what was just written back to Clio (when applicable)
              batchResult.clio_writeback?.written?.length > 0 && React.createElement("div", {
                style: { fontSize: "12px", color: fg, marginTop: "6px", paddingTop: "6px", borderTop: `1px solid ${fg}30` }
              },
                `Saved to Clio: ${batchResult.clio_writeback.written.map(w => w.clio_field_name).join(", ")}`,
              ),
            );
          })(),

          // Inline missing-fields panel \u2014 only shown when at least one
          // template was skipped due to missing data. Fields are deduped
          // across templates so the user fills each one once.
          (() => {
            const missing = dedupeMissingFields(batchResult);
            if (missing.length === 0) return null;
            return React.createElement("div", {
              style: {
                padding: "16px", borderRadius: "10px",
                background: `${C.gold}08`, border: `1px solid ${C.gold}40`,
                marginBottom: "16px",
              },
            },
              React.createElement("div", {
                style: { fontSize: "13px", fontWeight: 600, color: C.navy, marginBottom: "4px" }
              }, "Complete missing fields to retry skipped templates"),
              React.createElement("div", {
                style: { fontSize: "12px", color: C.textMuted, marginBottom: "12px", lineHeight: 1.4 }
              },
                missing.length === 1
                  ? "1 field is needed across the skipped templates. "
                  : `${missing.length} fields are needed across the skipped templates. `,
                "Fill these in once and click ",
                React.createElement("strong", null, "Retry skipped"),
                " \u2014 the values apply to every template that needed them.",
              ),
              ...missing.map(f => {
                const writable = f.source === "custom_field";
                return React.createElement("div", { key: f.field, style: { marginBottom: "10px" } },
                  React.createElement("label", {
                    style: { fontSize: "11px", color: C.textMuted, fontWeight: 600, display: "block", marginBottom: "4px", textTransform: "uppercase", letterSpacing: "0.04em" }
                  }, f.label || f.field),
                  React.createElement("input", {
                    type: f.field.includes("date") ? "date" : (f.field.includes("email") ? "email" : "text"),
                    value: batchOverrides[f.field] || "",
                    onChange: e => setBatchOverrides(prev => ({ ...prev, [f.field]: e.target.value })),
                    placeholder: f.label || f.field,
                    style: {
                      width: "100%", padding: "8px 12px", borderRadius: "6px",
                      border: `1px solid ${C.warmGray}`, fontSize: "13px",
                      color: C.textDark, background: "#fff",
                    },
                  }),
                  React.createElement("div", {
                    style: { fontSize: "11px", color: C.textLight, marginTop: "3px" }
                  },
                    "Source: ", f.source || "unknown",
                    writable && batchWriteBack && React.createElement("span", {
                      style: { color: C.gold, fontWeight: 500, marginLeft: "6px" }
                    }, "\u2192 will save to Clio"),
                    !writable && batchWriteBack && React.createElement("span", {
                      style: { color: C.textMuted, marginLeft: "6px" }
                    }, "(this run only)"),
                  ),
                );
              }),
              // Save-to-Clio toggle
              React.createElement("label", {
                style: {
                  display: "flex", alignItems: "flex-start", gap: "8px",
                  padding: "10px 12px", borderRadius: "6px",
                  background: batchWriteBack ? `${C.gold}10` : C.cream,
                  border: `1px solid ${batchWriteBack ? C.gold + "40" : C.warmGray}`,
                  cursor: "pointer", marginTop: "12px",
                },
              },
                React.createElement("input", {
                  type: "checkbox",
                  checked: batchWriteBack,
                  onChange: e => setBatchWriteBack(e.target.checked),
                  style: { marginTop: "3px", cursor: "pointer" },
                }),
                React.createElement("div", null,
                  React.createElement("div", {
                    style: { fontSize: "12px", fontWeight: 600, color: C.navy }
                  }, "Save these values to Clio"),
                  React.createElement("div", {
                    style: { fontSize: "11px", color: C.textMuted, lineHeight: 1.4, marginTop: "2px" }
                  },
                    "Writes the values back to the matter's custom fields so future batches auto-populate them.",
                  ),
                ),
              ),
              // Retry button
              React.createElement("button", {
                onClick: handleRetrySkipped,
                disabled: batchGenerating,
                style: {
                  marginTop: "12px", padding: "8px 18px", borderRadius: "8px", border: "none",
                  background: batchGenerating ? C.warmGray : C.gold, color: "#fff",
                  fontSize: "13px", fontWeight: 600,
                  cursor: batchGenerating ? "default" : "pointer",
                  display: "flex", alignItems: "center", gap: "6px",
                },
              },
                batchGenerating ? React.createElement(Spinner, { size: 14, color: "#fff" }) : I.zap,
                batchGenerating ? "Retrying..." : `Retry ${batchResult.results.filter(r => r.status === "skipped" && r.missing_fields?.length > 0).length} skipped template(s)`,
              ),
            );
          })(),

          batchResult.results.map(r =>
            React.createElement("div", { key: r.template, style: { display:"flex", alignItems:"center", gap:"10px", padding:"8px 0", borderBottom:`1px solid ${C.warmGray}20` } },
              React.createElement("div", {
                style: {
                  width:"18px", height:"18px", borderRadius:"50%", flexShrink:0,
                  background: r.status === "success" ? C.success : r.status === "skipped" ? C.goldDim : C.error,
                  display:"flex", alignItems:"center", justifyContent:"center",
                }
              },
                r.status === "success" && React.createElement("svg", {width:10,height:10,viewBox:"0 0 24 24",fill:"none",stroke:"#fff",strokeWidth:3,strokeLinecap:"round"},
                  React.createElement("polyline", {points:"20 6 9 17 4 12"})
                ),
                r.status === "skipped" && React.createElement("span", { style:{ color:"#fff", fontSize:"10px", fontWeight:700 } }, "—"),
                r.status === "failed" && React.createElement("span", { style:{ color:"#fff", fontSize:"12px", fontWeight:700 } }, "\u00d7"),
              ),
              React.createElement("div", { style: { flex:1 } },
                React.createElement("div", { style: { fontSize:"13px", fontWeight:500, color:C.navy } }, r.name || r.template),
                r.error && React.createElement("div", { style: { fontSize:"11px", color:C.textMuted, marginTop:"1px" } }, r.error),
                r.documents && r.documents.length > 0 && React.createElement("div", { style: { fontSize:"11px", color:C.textLight, marginTop:"1px" } },
                  r.documents.map(d => d.filename).join(", ")
                ),
              ),
            )
          ),
        ),
        !batchLoadingPkg && batchPackage && React.createElement("div", {
          style: { display:"flex", justifyContent:"flex-end", gap:"10px", borderTop:`1px solid ${C.warmGray}40`, paddingTop:"16px" }
        },
          React.createElement("button", {
            onClick: () => {
              setShowBatchModal(false);
              setBatchResult(null);
              setBatchOverrides({});
            },
            disabled: batchGenerating,
            style: { padding:"10px 20px", borderRadius:"10px", border:`1px solid ${C.warmGray}`, background:"#fff", color:C.textMuted, fontSize:"13px", fontWeight:600, cursor: batchGenerating ? "wait" : "pointer" }
          }, batchResult ? "Close" : "Cancel"),
          !batchResult && React.createElement("button", {
            onClick: handleBatchGenerate,
            disabled: batchGenerating || Object.values(batchSelections).filter(Boolean).length === 0,
            style: {
              padding:"10px 20px", borderRadius:"10px", border:"none",
              background: batchGenerating ? C.textMuted : C.navy, color:"#fff",
              fontSize:"13px", fontWeight:600,
              cursor: batchGenerating ? "wait" : "pointer",
              display:"flex", alignItems:"center", gap:"6px",
              opacity: Object.values(batchSelections).filter(Boolean).length === 0 ? 0.5 : 1,
            }
          },
            batchGenerating ? React.createElement(Spinner, { size:14, color:"#fff" }) : I.zap,
            batchGenerating
              ? ` Generating ${Object.values(batchSelections).filter(Boolean).length} document(s)...`
              : ` Generate ${Object.values(batchSelections).filter(Boolean).length} Document(s)`,
          ),
        ),
      ),
    ),
  );
}


